/**
 * PricingPage — public "View Cost Breakdown" page (WhatsApp deep-link target /pricing).
 * Shows the slab/tier cost calculator by student count and a "Pay Now" button that
 * opens the full Razorpay flow (SubscriptionPaymentModal). 5000+ → Contact Sales.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePricingConfig } from '../hooks/usePricingConfig';
import SubscriptionPaymentModal from '../components/SubscriptionPaymentModal';

const LOGO = 'https://customer-assets.emergentagent.com/job_b71b88a0-de31-4e82-b047-c99df2477168/artifacts/7dlw52ts_logo%20%281%29.jpg';

export default function PricingPage() {
  const navigate = useNavigate();
  const { config: pricingCfg } = usePricingConfig();
  const [language, setLanguage] = useState('hi');
  const [students, setStudents] = useState('');
  const [showPay, setShowPay] = useState(false);

  const CURR = pricingCfg.currency_symbol || '₹';
  const tiers = pricingCfg.pricing_tiers || [];
  const salesPhone = pricingCfg.sales_contact_phone || '9826618603';
  const count = parseInt(students, 10) || 0;
  const tier = count > 0 ? tiers.find((x) => count >= x.min && (x.max == null || count <= x.max)) : null;
  const isCustom = !!tier?.custom;
  const oneTime = tier && !isCustom ? tier.one_time : 0;
  const maint = tier && !isCustom ? tier.annual_maintenance : 0;
  const total = oneTime + maint;
  const fmt = (n) => `${CURR}${Number(n).toLocaleString('en-IN')}`;
  const t = (hi, en) => (language === 'hi' ? hi : en);

  const salesUrl = `https://wa.me/91${salesPhone}?text=${encodeURIComponent(t(`नमस्ते! हमारे स्कूल में ${count || '5000+'} कुल users (छात्र+शिक्षक+staff) हैं। कृपया Shiksha Mitra custom pricing बताएँ।`, `Hi! Our school has ${count || '5000+'} total users (students+teachers+staff). Please share Shiksha Mitra custom pricing.`))}`;

  return (
    <div className="min-h-screen bg-[#FFFDF9]" data-testid="pricing-page">
      {/* Header */}
      <header className="bg-white border-b border-orange-100 sticky top-0 z-20">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-2" data-testid="pricing-home-link">
            <img src={LOGO} alt="Shiksha Mitra" className="h-9 w-9 rounded-lg object-cover" />
            <span className="font-black text-slate-800">शिक्षा मित्र</span>
          </button>
          <button onClick={() => setLanguage(language === 'hi' ? 'en' : 'hi')} className="text-sm px-3 py-1.5 rounded-full bg-orange-50 text-orange-700 font-semibold" data-testid="pricing-lang-toggle">
            {language === 'hi' ? 'English' : 'हिंदी'}
          </button>
        </div>
      </header>

      <main className="max-w-xl mx-auto px-4 py-8">
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-green-100 text-green-700 rounded-full text-xs font-black uppercase tracking-wide mb-3">
            <i className="fa-solid fa-gift"></i> 100% FREE Platform
          </div>
          <h1 className="text-3xl font-black text-slate-800 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {t('अपनी Cost देखें', 'View Your Cost')}
          </h1>
          <p className="text-slate-500 text-sm">
            {t('Software lifetime FREE — plan आपके कुल users (छात्र+शिक्षक+staff) पर आधारित।', 'Software lifetime FREE — plan based on your total users (students+teachers+staff).')}
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
          <div className="bg-gradient-to-r from-orange-500 to-amber-500 px-6 py-4 text-white text-center">
            <h2 className="text-lg font-bold">{t('Pricing Calculator', 'Pricing Calculator')}</h2>
            <p className="text-sm text-white/80">{t('One-time + सालाना maintenance', 'One-time + yearly maintenance')}</p>
          </div>

          <div className="p-6 space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-600 block mb-2">{t('आपके कुल users (छात्र + शिक्षक + staff)', 'Total users (students + teachers + staff)')}</label>
              <input type="number" min="0" value={students}
                onChange={(e) => setStudents(e.target.value.replace(/[^0-9]/g, ''))}
                placeholder={t('जैसे: 250', 'e.g. 250')}
                className="w-full px-4 py-3 border-2 rounded-xl text-2xl font-bold text-center focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                data-testid="pricing-page-students" />
              <div className="flex flex-wrap gap-2 mt-3 justify-center">
                {[200, 500, 1000, 2000, 4000].map((c) => (
                  <button key={c} onClick={() => setStudents(String(c))}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition ${count === c ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-orange-100'}`}>{c}</button>
                ))}
              </div>
            </div>

            {isCustom ? (
              <div className="bg-slate-900 text-white rounded-xl p-5 text-center" data-testid="pricing-page-custom">
                <p className="font-semibold text-amber-300 mb-1">{t('🏛️ बड़े स्कूल (5000+ users)', '🏛️ Large school (5000+ users)')}</p>
                <p className="text-sm text-gray-300 mb-3">{t('आपके स्कूल के लिए custom pricing है।', 'Custom pricing for your school.')}</p>
                <a href={salesUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-5 py-2.5 bg-green-500 hover:bg-green-600 rounded-lg font-bold transition" data-testid="pricing-page-contact-sales">
                  <i className="fa-brands fa-whatsapp"></i> {t('Sales से संपर्क करें', 'Contact Sales')}
                </a>
              </div>
            ) : (
              <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl p-5 border border-orange-200">
                {tier && (
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-gray-600">{t('आपका plan', 'Your plan')}</span>
                    <span className="text-sm font-bold text-gray-800" data-testid="pricing-page-tier">{t(tier.label_hi, tier.label_en)}</span>
                  </div>
                )}
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm text-gray-600">{t('One-time (license + setup)', 'One-time (license + setup)')}</span>
                  <span className="text-sm font-semibold">{count > 0 ? fmt(oneTime) : '—'}</span>
                </div>
                <div className="flex justify-between items-center mb-3">
                  <span className="text-sm text-gray-600">{t('सालाना maintenance', 'Annual maintenance')}</span>
                  <span className="text-sm font-semibold">{count > 0 ? `${fmt(maint)}${t('/साल', '/yr')}` : '—'}</span>
                </div>
                <div className="border-t border-orange-200 pt-3 flex justify-between items-center">
                  <span className="font-bold text-gray-800">{t('अभी देय (पहला भुगतान)', 'Payable now (first payment)')}</span>
                  <span className="text-3xl font-black text-orange-600" data-testid="pricing-page-total">{count > 0 ? fmt(total) : `${CURR}0`}</span>
                </div>
              </div>
            )}

            <p className="text-xs text-center text-gray-400 px-2">
              {t('Software 100% FREE • AI FREE (BYOK) • कोई per-user charge नहीं। अगले साल से सिर्फ़ maintenance।', 'Software 100% FREE • AI FREE (BYOK) • No per-user charge. Only maintenance from next year.')}
            </p>

            {!isCustom && (
              <button onClick={() => setShowPay(true)} disabled={count < 1}
                className="w-full py-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl font-bold text-lg hover:from-green-600 hover:to-emerald-700 transition shadow-lg disabled:opacity-50"
                data-testid="pricing-page-pay-now">
                {count > 0 ? `${fmt(total)} ${t('— अभी भुगतान करें', '— Pay Now')}` : t('अभी भुगतान करें', 'Pay Now')}
              </button>
            )}
          </div>
        </div>
      </main>

      <SubscriptionPaymentModal
        isOpen={showPay}
        onClose={() => setShowPay(false)}
        language={language}
        prefillData={{ studentCount: count, source: 'pricing_page' }}
        source="pricing_page"
      />
    </div>
  );
}
